import {SelectChangeEvent} from "@mui/material";
import {Bindings} from "data/constants/bindings";
import {RoundType} from "data/enums";
import {ILineup, IPickResponse} from "data/providers/api/pinpoint.api.provider";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPinpointStore} from "data/stores/pinpoint/pinpoint.store";
import type {IPlayerPoolStore} from "data/stores/player_pool/player_pool.store";
import type {IRoundsStore, ISessionWithPick} from "data/stores/rounds/rounds.store";
import {ViewController} from "data/types/structure";
import {getIsRoundFoursomes} from "data/utils/helper";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {IPickProps} from "views/components/pick/pick.component";

export interface IPickController extends ViewController<IPickProps> {
	get i18n(): ILocalizationStore;

	get round(): ISessionWithPick | null;
	get pick(): IPickResponse | null;
	get data(): {
		selectValue: string;
		playerIds: number[];
		hole: number;
		par: number;
		yards: number;
	};
	get isRoundFoursomes(): boolean;

	onSelectChange: (event: SelectChangeEvent<string>) => void;
	onOpenPoolModal: () => void;
}

@injectable()
export class PickController implements IPickController {
	@observable _roundId?: number | null;
	@observable _type: RoundType = RoundType.LONGESTDRIVE;

	constructor(
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.PinpointStore) private _pinpointStore: IPinpointStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.PlayerPoolStore) private _poolStore: IPlayerPoolStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get round() {
		if (!this._roundId) {
			return null;
		}

		return this._roundStore.getRoundById(this._roundId);
	}

	get pick() {
		if (!this._roundId) {
			return null;
		}

		return this._pinpointStore.getPickByRoundId(this._roundId);
	}

	get type() {
		return this._type;
	}

	get data() {
		return {
			[RoundType.LONGESTDRIVE]: {
				selectValue: this.pick?.longestDriveLineup?.value || "",
				playerIds: this.pick?.longestDriveLineup?.golfers || [],
				hole: 9,
				par: 5,
				yards: 587,
			},
			[RoundType.NEARESTPIN]: {
				selectValue: this.pick?.nearestPinLineup?.value || "",
				playerIds: this.pick?.nearestPinLineup?.golfers || [],
				hole: 13,
				par: 3,
				yards: 150,
			},
		}[this.type];
	}

	get isRoundFoursomes() {
		return getIsRoundFoursomes(this.round);
	}

	onSelectChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;

		const updatedPick = {
			...this.pick,
			...(this._type === RoundType.LONGESTDRIVE && {
				longestDriveLineup: {
					...(this.pick?.longestDriveLineup || {}),
					value,
				} as ILineup,
			}),
			...(this._type === RoundType.NEARESTPIN && {
				nearestPinLineup: {
					...(this.pick?.nearestPinLineup || {}),
					value,
				} as ILineup,
			}),
		};

		this._pinpointStore.updatePickByRoundId(this._roundId!, updatedPick);
	};

	onOpenPoolModal = () => {
		this._poolStore.togglePoolModal({type: this._type});
	};

	init({roundId, type}: IPickProps) {
		this._roundId = roundId;
		this._type = type;
	}
}
